import apiCall from './api';

/**
 * Service pour la gestion des promotions
 * ✅ CORRIGÉ : updatePromotion gère correctement l'ajout/suppression des produits
 */
export const promotionService = {

  // ==================== CRUD PROMOTIONS ====================

  /**
   * Créer une nouvelle promotion
   */
  createPromotion: async (promotionData) => {
    try {

      const payload = {
        name: promotionData.name,
        description: promotionData.description || null,
        promotion_type: promotionData.promotionType,
        start_date: promotionData.startDate || null,
        end_date: promotionData.endDate || null,
        remaining_sales: promotionData.remainingSales || 1,
        apply_to_all: promotionData.applyToAll || false,
        discount_percentage: promotionData.discountPercentage || null,
        discount_amount: promotionData.discountAmount || null,
        products: (promotionData.products || []).map(p => ({
          product_id: p.productId,
          promo_price: p.promoPrice,
          discount_percentage: p.discountPercentage || null
        })),
        activate_now: promotionData.activateNow || false
      };


      const response = await apiCall.post('/manager/promotions', payload);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion créée avec succès'
        };
      }

      return { success: false, message: response.message || 'Erreur lors de la création' };

    } catch (error) {
      console.error('❌ Erreur création promotion:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  /**
   * Récupérer toutes les promotions
   */
  getAllPromotions: async () => {
    try {

      const response = await apiCall.get('/manager/promotions');

      if (response.success && Array.isArray(response.data)) {
        const promotions = response.data.map(p => promotionService.formatPromotionForDisplay(p));
        return { success: true, data: promotions };
      }

      return { success: false, data: [], message: 'Aucune promotion trouvée' };

    } catch (error) {
      console.error('❌ Erreur récupération promotions:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Récupérer les promotions actives
   */
  getActivePromotions: async () => {
    try {

      const response = await apiCall.get('/manager/promotions/active');

      if (response.success && Array.isArray(response.data)) {
        const promotions = response.data.map(p => promotionService.formatPromotionForDisplay(p));
        return { success: true, data: promotions };
      }

      return { success: false, data: [], message: 'Aucune promotion active' };

    } catch (error) {
      console.error('❌ Erreur récupération promotions actives:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Récupérer une promotion par ID
   */
  getPromotionById: async (promotionId) => {
    try {

      const response = await apiCall.get(`/manager/promotions/${promotionId}`);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data)
        };
      }

      return { success: false, data: null, message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération promotion:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  /**
   * Mettre à jour une promotion
   * ✅ CORRIGÉ : Calcule automatiquement productsToAdd et productIdsToRemove
   *    à partir de la liste products du formulaire vs les produits existants
   */
  updatePromotion: async (promotionId, updateData, existingProducts = []) => {
    try {
      

      const payload = {};
      
      if (updateData.name !== undefined) payload.name = updateData.name;
      if (updateData.description !== undefined) payload.description = updateData.description;
      if (updateData.promotionType !== undefined) payload.promotion_type = updateData.promotionType;
      if (updateData.status !== undefined) payload.status = updateData.status;
      if (updateData.startDate !== undefined) payload.start_date = updateData.startDate || null;
      if (updateData.endDate !== undefined) payload.end_date = updateData.endDate || null;
      if (updateData.remainingSales !== undefined) payload.remaining_sales = updateData.remainingSales;
      if (updateData.applyToAll !== undefined) payload.apply_to_all = updateData.applyToAll;
      if (updateData.discountPercentage !== undefined) payload.discount_percentage = updateData.discountPercentage || null;
      if (updateData.discountAmount !== undefined) payload.discount_amount = updateData.discountAmount || null;

      // ✅ CORRECTION PRINCIPALE : Calculer les produits à ajouter et retirer
      // à partir de la liste "products" du formulaire
      const formProducts = updateData.products || [];
      
      // Si on a des produits dans le formulaire, calculer les diffs
      if (formProducts.length > 0 || existingProducts.length > 0) {
        const existingProductIds = existingProducts.map(p => p.productId);
        const formProductIds = formProducts.map(p => p.productId);

        // Produits à ajouter = ceux dans le formulaire mais pas dans les existants
        const productsToAdd = formProducts.filter(p => !existingProductIds.includes(p.productId));
        
        // Produits à retirer = ceux dans les existants mais plus dans le formulaire
        const productIdsToRemove = existingProductIds.filter(id => !formProductIds.includes(id));

        if (productsToAdd.length > 0) {
          payload.products_to_add = productsToAdd.map(p => ({
            product_id: p.productId,
            promo_price: p.promoPrice,
            discount_percentage: p.discountPercentage || null
          }));
        }

        if (productIdsToRemove.length > 0) {
          payload.product_ids_to_remove = productIdsToRemove;
        }
      }

      // Aussi supporter l'ancien format si utilisé directement
      if (updateData.productsToAdd && updateData.productsToAdd.length > 0) {
        payload.products_to_add = updateData.productsToAdd.map(p => ({
          product_id: p.productId,
          promo_price: p.promoPrice,
          discount_percentage: p.discountPercentage || null
        }));
      }
      
      if (updateData.productIdsToRemove && updateData.productIdsToRemove.length > 0) {
        payload.product_ids_to_remove = updateData.productIdsToRemove;
      }


      const response = await apiCall.put(`/manager/promotions/${promotionId}`, payload);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion mise à jour'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur mise à jour promotion:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Supprimer une promotion
   */
  deletePromotion: async (promotionId) => {
    try {

      const response = await apiCall.delete(`/manager/promotions/${promotionId}`);

      if (response.success) {
        return { success: true, message: 'Promotion supprimée' };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur suppression promotion:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== ACTIVATION / DÉSACTIVATION ====================

  /**
   * Activer une promotion
   */
  activatePromotion: async (promotionId) => {
    try {

      const response = await apiCall.post(`/manager/promotions/${promotionId}/activate`);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion activée'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur activation promotion:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Désactiver une promotion
   */
  deactivatePromotion: async (promotionId) => {
    try {

      const response = await apiCall.post(`/manager/promotions/${promotionId}/deactivate`);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion désactivée'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur désactivation promotion:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== GESTION DES PRODUITS ====================

  /**
   * Ajouter un produit à une promotion
   */
  addProductToPromotion: async (promotionId, productData) => {
    try {

      const payload = {
        product_id: productData.productId,
        promo_price: productData.promoPrice,
        discount_percentage: productData.discountPercentage || null
      };

      const response = await apiCall.post(`/manager/promotions/${promotionId}/products`, payload);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionProductForDisplay(response.data),
          message: 'Produit ajouté à la promotion'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur ajout produit:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Retirer un produit d'une promotion
   */
  removeProductFromPromotion: async (promotionId, productId) => {
    try {

      const response = await apiCall.delete(`/manager/promotions/${promotionId}/products/${productId}`);

      if (response.success) {
        return { success: true, message: 'Produit retiré de la promotion' };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur retrait produit:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Mettre à jour le prix promo d'un produit
   */
  updateProductPromoPrice: async (promotionId, productId, newPromoPrice) => {
    try {

      const response = await apiCall.patch(
        `/manager/promotions/${promotionId}/products/${productId}`,
        { promo_price: newPromoPrice }
      );

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionProductForDisplay(response.data),
          message: 'Prix promo mis à jour'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur mise à jour prix promo:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== PRIX PROMO ACTIFS (POUR POS) ====================

  /**
   * Récupérer tous les prix promo actifs
   * Utilisé par le POS pour afficher les prix promo
   */
  getActivePromoPrices: async () => {
    try {

      const response = await apiCall.get('/manager/promotions/active-prices');

      if (response.success) {
        return { success: true, data: response.data };
      }

      return { success: false, data: {}, message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération prix promo:', error);
      return { success: false, data: {}, message: error.message };
    }
  },

  // ==================== FORMATAGE ====================

  /**
   * Formater une promotion pour l'affichage
   */
  formatPromotionForDisplay: (promotion) => {
    if (!promotion) return null;

    return {
      id: promotion.id,
      name: promotion.name,
      description: promotion.description,
      
      promotionType: promotion.promotion_type || promotion.promotionType,
      promotionTypeLabel: promotion.promotion_type_label || promotion.promotionTypeLabel || 
        promotionService.getPromotionTypeLabel(promotion.promotion_type || promotion.promotionType),
      
      status: promotion.status,
      statusLabel: promotion.status_label || promotion.statusLabel ||
        promotionService.getStatusLabel(promotion.status),
      statusColor: promotionService.getStatusColor(promotion.status),
      
      startDate: promotion.start_date || promotion.startDate,
      endDate: promotion.end_date || promotion.endDate,
      remainingSales: promotion.remaining_sales || promotion.remainingSales,
      
      applyToAll: promotion.apply_to_all || promotion.applyToAll || false,
      discountPercentage: promotion.discount_percentage || promotion.discountPercentage,
      discountAmount: promotion.discount_amount || promotion.discountAmount,
      
      boutiqueId: promotion.boutique_id || promotion.boutiqueId,
      createdById: promotion.created_by_id || promotion.createdById,
      createdByName: promotion.created_by_name || promotion.createdByName,
      
      products: (promotion.products || []).map(p => promotionService.formatPromotionProductForDisplay(p)),
      productCount: promotion.product_count || promotion.productCount || (promotion.products || []).length,
      totalSavings: promotion.total_savings || promotion.totalSavings || 0,
      
      isCurrentlyActive: promotion.is_currently_active || promotion.isCurrentlyActive || false,
      isExpired: promotion.is_expired || promotion.isExpired || false,
      
      usedInSaleId: promotion.used_in_sale_id || promotion.usedInSaleId,
      usedAt: promotion.used_at || promotion.usedAt,
      
      createdAt: promotion.created_at || promotion.createdAt,
      updatedAt: promotion.updated_at || promotion.updatedAt
    };
  },

  /**
   * Formater un produit de promotion pour l'affichage
   */
  formatPromotionProductForDisplay: (pp) => {
    if (!pp) return null;

    return {
      id: pp.id,
      promotionId: pp.promotion_id || pp.promotionId,
      productId: pp.product_id || pp.productId,
      productName: pp.product_name || pp.productName,
      productSku: pp.product_sku || pp.productSku,
      productBarcode: pp.product_barcode || pp.productBarcode,
      productImageUrl: pp.product_image_url || pp.productImageUrl,
      categoryName: pp.category_name || pp.categoryName,
      originalPrice: parseFloat(pp.original_price || pp.originalPrice || 0),
      promoPrice: parseFloat(pp.promo_price || pp.promoPrice || 0),
      discountPercentage: parseFloat(pp.discount_percentage || pp.discountPercentage || 0),
      savings: parseFloat(pp.savings || 0),
      currentStock: pp.current_stock || pp.currentStock,
      isActive: pp.is_active || pp.isActive
    };
  },

  // ==================== UTILITAIRES ====================

  /**
   * Obtenir le label du type de promotion
   */
  getPromotionTypeLabel: (type) => {
    const labels = {
      'PERIOD': 'Période',
      'NEXT_SALE': 'Prochaine vente'
    };
    return labels[type] || type;
  },

  /**
   * Obtenir le label du statut
   */
  getStatusLabel: (status) => {
    const labels = {
      'DRAFT': 'Brouillon',
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'EXPIRED': 'Expirée',
      'COMPLETED': 'Terminée'
    };
    return labels[status] || status;
  },

  /**
   * Obtenir la couleur du statut
   */
  getStatusColor: (status) => {
    const colors = {
      'DRAFT': 'secondary',
      'ACTIVE': 'success',
      'INACTIVE': 'warning',
      'EXPIRED': 'danger',
      'COMPLETED': 'info'
    };
    return colors[status] || 'secondary';
  },

  /**
   * Obtenir l'icône du type de promotion
   */
  getPromotionTypeIcon: (type) => {
    const icons = {
      'PERIOD': '📅',
      'NEXT_SALE': '🎯'
    };
    return icons[type] || '🏷️';
  },

  /**
   * Formater un montant en monnaie
   */
  formatCurrency: (amount) => {
    if (amount === null || amount === undefined) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0
    }).format(amount) + ' FCFA';
  }
};

export default promotionService;